import React from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import Button from '../components/ui/Button';
import { FaShoppingCart, FaTruck, FaEdit, FaKey, FaLink, FaRocket, FaQrcode, FaCheckCircle, FaMobileAlt } from 'react-icons/fa';
import * as FiIcons from 'react-icons/fi';

const { FiArrowRight, FiArrowLeft, FiMenu, FiX } = FiIcons;

const HowItWorks = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const steps = [
    {
      number: 1,
      icon: FaShoppingCart,
      title: "Buy Your Card",
      description: "Choose your design and place your order via WhatsApp. We offer premium quality smart cards with sleek designs.",
      details: [
        "Contact us on WhatsApp",
        "Choose your preferred design",
        "Complete your order",
        "Get order confirmation"
      ],
      color: "from-blue-600 to-blue-800",
      rotate: "-rotate-6"
    },
    {
      number: 2,
      icon: FaTruck,
      title: "Get It Delivered",
      description: "We'll ship your premium smart card directly to your doorstep with secure packaging.",
      details: [
        "Fast shipping across India",
        "Secure packaging",
        "Track your order",
        "Receive your card in 3-5 days"
      ],
      color: "from-green-600 to-green-800",
      rotate: "rotate-3"
    },
    {
      number: 3,
      icon: FaEdit,
      title: "Click Edit Card",
      description: "Navigate to the Edit Card section on our website to start configuring your smart card.",
      details: [
        "Visit connectkard.com",
        "Click on 'Edit Card' button",
        "You'll be asked for verification",
        "Get ready to configure"
      ],
      color: "from-purple-600 to-purple-800",
      rotate: "-rotate-3"
    },
    {
      number: 4,
      icon: FaKey,
      title: "Enter Your Code",
      description: "Add the 6-digit OTP from your card, or scan the QR code if it's your first time setting up.",
      details: [
        "Find the 6-digit code on your card",
        "Enter it on the website",
        "Or scan the QR code for first-time setup",
        "Verification complete!"
      ],
      color: "from-yellow-600 to-yellow-800",
      rotate: "rotate-6"
    },
    {
      number: 5,
      icon: FaLink,
      title: "Add Your URL",
      description: "Enter your website, social media, or any destination link you want to share.",
      details: [
        "Choose between Direct Link or Smart Profile",
        "Add your website or social media links",
        "Add company name and logo (Smart Profile)",
        "Customize your profile"
      ],
      color: "from-pink-600 to-pink-800",
      rotate: "-rotate-6"
    },
    {
      number: 6,
      icon: FaRocket,
      title: "You're Ready!",
      description: "Save your settings and start sharing your smart card instantly. Update anytime!",
      details: [
        "Click 'Save Changes'",
        "Your card is now live",
        "Share with anyone",
        "Update anytime you want"
      ],
      color: "from-red-600 to-red-800",
      rotate: "rotate-3"
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white font-sans selection:bg-red-600 selection:text-white">
      {/* Navbar */}
      <nav className="border-b border-white/10 bg-black/50 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl flex items-center justify-center transform -rotate-6 shadow-xl shadow-red-900/40 hover:rotate-0 transition-transform duration-300">
              <FaQrcode className="text-white text-2xl" />
            </div>
            <span className="font-bold text-2xl tracking-tight">
              Connect<span className="text-red-500">Kard</span>
            </span>
          </Link>
          
          <div className="flex items-center gap-3">
            {/* Desktop Buttons */}
            <Link to="/" className="hidden sm:block">
              <Button variant="outline" className="px-6 py-2.5 text-sm bg-black/50 border-white/20 hover:bg-white/10">
                <SafeIcon icon={FiArrowLeft} />
                Back to Home
              </Button>
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="sm:hidden w-10 h-10 flex items-center justify-center text-white hover:text-red-500 transition-colors"
            >
              <SafeIcon icon={mobileMenuOpen ? FiX : FiMenu} className="text-2xl" />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="sm:hidden bg-black/95 backdrop-blur-md border-t border-white/10 animate-in slide-in-from-top duration-300">
            <div className="px-4 py-6 space-y-4">
              <Link to="/" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="outline" className="w-full py-3 text-sm bg-black/50 border-white/20 hover:bg-white/10">
                  <SafeIcon icon={FiArrowLeft} />
                  Back to Home
                </Button>
              </Link>
              <Link to="/editqr" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="outline" className="w-full py-3 text-sm bg-black/50 border-white/20 hover:bg-white/10">
                  <SafeIcon icon={FaEdit} />
                  Edit Card
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-red-950/20 via-black to-black" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center space-y-6 mb-20 animate-in fade-in slide-in-from-bottom duration-700">
            <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600/20 border border-red-500/30 rounded-full text-red-400 text-sm font-bold backdrop-blur-md">
              <SafeIcon icon={FaCheckCircle} />
              <span>Simple 6-Step Process</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tight">
              How It <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-400">Works</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-zinc-400 max-w-3xl mx-auto leading-relaxed">
              Get your smart business card up and running in minutes. Follow these simple steps to start networking smarter.
            </p>
          </div>

          {/* Steps Grid */}
          <div className="space-y-16 md:space-y-24">
            {steps.map((step, index) => (
              <div 
                key={step.number}
                className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 md:gap-16 items-center animate-in fade-in slide-in-from-${index % 2 === 0 ? 'left' : 'right'}-8 duration-700`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Icon Section */}
                <div className="flex-shrink-0 w-full md:w-1/3 flex justify-center">
                  <div className="relative">
                    <div className={`w-48 h-48 bg-gradient-to-br ${step.color} rounded-3xl flex items-center justify-center shadow-2xl shadow-red-900/40 transform ${step.rotate} hover:rotate-0 transition-all duration-500 relative overflow-hidden group`}>
                      <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <SafeIcon icon={step.icon} className="text-7xl text-white relative z-10 group-hover:scale-110 transition-transform duration-300" />
                    </div>
                    
                    {/* Step Number Badge */}
                    <div className="absolute -top-4 -right-4 w-16 h-16 bg-red-600 rounded-full flex items-center justify-center shadow-xl border-4 border-black">
                      <span className="text-2xl font-black text-white">{step.number}</span>
                    </div>
                  </div>
                </div>

                {/* Content Section */}
                <div className="flex-1 w-full space-y-6">
                  <div className="space-y-4">
                    <h2 className="text-4xl md:text-5xl font-black">{step.title}</h2>
                    <p className="text-xl text-zinc-400 leading-relaxed">
                      {step.description}
                    </p>
                  </div>

                  {/* Details List */}
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 space-y-3">
                    {step.details.map((detail, idx) => (
                      <div 
                        key={idx}
                        className="flex items-start gap-3 group hover:translate-x-2 transition-transform duration-300"
                      >
                        <div className="flex-shrink-0 w-6 h-6 bg-red-600/20 rounded-full flex items-center justify-center mt-0.5">
                          <SafeIcon icon={FaCheckCircle} className="text-red-500 text-sm" />
                        </div>
                        <p className="text-zinc-300 leading-relaxed">{detail}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-red-950/30 via-black to-zinc-950 relative overflow-hidden border-t border-white/10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-red-900/20 via-transparent to-transparent animate-pulse" />
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="space-y-8 animate-in fade-in slide-in-from-bottom duration-700">
            <div className="w-20 h-20 bg-red-600 rounded-2xl flex items-center justify-center mx-auto shadow-2xl shadow-red-900/40 transform -rotate-6">
              <SafeIcon icon={FaMobileAlt} className="text-4xl text-white" />
            </div>
            
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black">
              Ready to Get Started?
            </h2>
            
            <p className="text-xl md:text-2xl text-zinc-400 max-w-3xl mx-auto leading-relaxed">
              Order your ConnectKard today and join the future of networking!
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center pt-4">
              <a 
                href="https://wa.me/917796983999?text=I%20want%20to%20buy%20ConnectKard" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-full sm:w-auto"
              >
                <Button className="w-full sm:w-auto px-12 py-6 text-xl font-bold shadow-2xl shadow-red-900/50 hover:shadow-red-900/70 transform hover:scale-105 transition-all duration-300">
                  <SafeIcon icon={FaShoppingCart} />
                  Order Now
                  <SafeIcon icon={FiArrowRight} />
                </Button>
              </a>
              
              <Link to="/editqr" className="w-full sm:w-auto">
                <Button variant="outline" className="w-full sm:w-auto px-12 py-6 text-xl font-bold border-2 bg-black/40 backdrop-blur-sm border-zinc-500 hover:bg-white/10 hover:border-white text-white transition-all duration-300">
                  <SafeIcon icon={FaEdit} />
                  Edit My Card
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800/50 bg-zinc-950 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3 hover:scale-105 transition-transform duration-300">
              <div className="w-10 h-10 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center transform -rotate-6 hover:rotate-0 transition-transform duration-300">
                <FaQrcode className="text-white text-xl" />
              </div>
              <span className="font-bold text-xl">
                Connect<span className="text-red-500">Kard</span>
              </span>
            </div>
            
            <p className="text-zinc-500 text-sm">
              © 2026 ConnectKard. All rights reserved.
            </p>
            
            <div className="flex gap-8 text-sm text-zinc-400 font-medium">
              <Link to="/" className="hover:text-white transition-colors duration-300">Home</Link>
              <a href="/#contact" className="hover:text-white transition-colors duration-300">Contact</a>
              <Link to="/admin/login" className="hover:text-white transition-colors duration-300">Admin</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HowItWorks;